import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2, ShoppingBag, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, money, errMsg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSEO } from "@/hooks/useSEO";

const box = "rounded-xl border border-white/10 bg-[#0F1117] p-5 sm:p-6 space-y-4";
const OPEN = ["awaiting_payment", "expired"];

const PayAgain = ({ order }) => {
  const { add, clear } = useCart();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      if (order.status === "awaiting_payment") await api.post(`/orders/${order.id}/cancel`, { email: order.email });
      const games = await Promise.all(order.items.map((i) => api.get(`/games/${i.game_id}`).then((r) => [r.data, i.qty])));
      clear();
      games.forEach(([g, q]) => add(g, q));
      navigate(`/checkout?email=${encodeURIComponent(order.email)}`);
    } catch (e) { toast.error(errMsg(e, "Some games are no longer available. Please contact us.")); setBusy(false); }
  };
  return (
    <section className={box}>
      <p className="font-display font-semibold text-lg flex items-center gap-2"><ShoppingBag size={18} className="text-emerald-400" /> Haven't paid yet?</p>
      <p className="text-sm text-gray-400">Start a fresh checkout with the same games. You'll get a new 5-minute payment window.</p>
      <button data-testid="complete-pay-again" onClick={go} disabled={busy} className="w-full sm:w-auto h-11 px-5 rounded-md bg-emerald-500 text-[#08090C] font-bold inline-flex items-center justify-center gap-2 hover:bg-emerald-400 disabled:opacity-50">
        {busy && <Loader2 size={16} className="animate-spin" />} Complete my order
      </button>
    </section>
  );
};

const SubmitHash = ({ order, onDone }) => {
  const [tx, setTx] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { onDone((await api.post(`/orders/${order.id}/confirm`, { email: order.email, tx_hash: tx.trim() })).data); toast.success("Thanks! We'll verify your payment."); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className={box}>
      <p className="font-display font-semibold text-lg flex items-center gap-2"><ShieldCheck size={18} className="text-amber-400" /> Already paid?</p>
      <p className="text-sm text-gray-400">Paste the transaction ID (TXID) of your USDT payment for {money(order.total)}. We'll check it and email your codes.</p>
      <input data-testid="complete-tx-input" required minLength={10} value={tx} onChange={(e) => setTx(e.target.value)} placeholder="Transaction ID / hash"
        className="w-full h-11 px-3 rounded-md bg-[#08090C] border border-white/10 font-mono text-sm focus:outline-none focus:border-amber-500/60" />
      <button data-testid="complete-tx-submit" disabled={busy} className="w-full sm:w-auto h-11 px-5 rounded-md border border-amber-500/60 text-amber-300 font-bold inline-flex items-center justify-center gap-2 hover:bg-amber-500/10 disabled:opacity-50">
        {busy && <Loader2 size={16} className="animate-spin" />} Submit transaction ID
      </button>
    </form>
  );
};

export default function CompleteOrder() {
  useSEO({ title: "Complete your order" });
  const { id } = useParams();
  const email = useSearchParams()[0].get("email") || "";
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/orders/lookup", { params: { order_id: id, email } }).then((r) => setOrder(r.data)).catch((e) => setError(errMsg(e)));
  }, [id, email]);
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 pt-8 sm:pt-12 pb-16 space-y-5">
      <p className="label-eyebrow text-emerald-400">Your order</p>
      <h1 className="font-display font-bold tracking-tight text-2xl sm:text-4xl">Finish your order</h1>
      {error && <p data-testid="complete-error" className="text-red-400 text-sm">{error}</p>}
      {!order && !error && <Loader2 className="animate-spin text-gray-500" />}
      {order && (
        <>
          <div data-testid="complete-summary" className={box}>
            <p className="font-mono text-sm text-gray-400">{order.id}</p>
            {order.items.map((i) => <div key={i.game_id} className="flex justify-between gap-3 text-sm"><span>{i.name} × {i.qty}</span><span className="font-mono">{money(i.price * i.qty)}</span></div>)}
            <div className="flex justify-between font-bold border-t border-white/10 pt-3"><span>Total</span><span data-testid="complete-total" className="font-mono text-emerald-400">{money(order.total)} USDT</span></div>
          </div>
          {OPEN.includes(order.status) ? (
            <><PayAgain order={order} /><SubmitHash order={order} onDone={setOrder} /></>
          ) : (
            <p data-testid="complete-status" className={`${box} text-sm text-gray-300`}>
              {order.status === "pending" ? "We've received your transaction ID and are verifying it. Your codes will be emailed once confirmed." : "This order is closed."}{" "}
              <Link to={`/order?id=${order.id}&email=${encodeURIComponent(order.email)}`} className="text-emerald-400 underline">Track it here</Link>.
            </p>
          )}
        </>
      )}
    </div>
  );
}
